/**
 * SetAvailabilityModal Component
 *
 * Modal for crew members to set their availability for an event.
 */

import { useState } from 'react';
import { setAvailability, type AvailabilityStatus } from '../../api/availability';
import { getErrorMessage } from '../../api/client';
import { AVAILABILITY_OPTIONS } from '../../config/statusStyles';

interface SetAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  eventId: string;
  eventName: string;
  currentStatus?: AvailabilityStatus | null;
  currentNotes?: string | null;
}

export default function SetAvailabilityModal({
  isOpen,
  onClose,
  onSuccess,
  eventId,
  eventName,
  currentStatus,
  currentNotes,
}: SetAvailabilityModalProps) {
  const [status, setStatus] = useState<AvailabilityStatus | null>(currentStatus || null);
  const [notes, setNotes] = useState(currentNotes || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!status) {
      setError('Please select your availability');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await setAvailability(eventId, {
        status,
        notes: notes.trim() || null,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setStatus(currentStatus || null);
      setNotes(currentNotes || '');
      setError(null);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white max-w-md w-full border border-hairline">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Set Availability</h3>
          <p className="text-sm text-gray-500 mt-1">{eventName}</p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Status Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Your Availability
            </label>
            <div className="space-y-2">
              {AVAILABILITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatus(option.value)}
                  className={`w-full p-3 border-2 rounded-lg text-left transition-colors ${
                    status === option.value
                      ? option.color + ' border-opacity-100'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium text-gray-900">{option.label}</p>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-rack focus:border-rack"
              placeholder="e.g., Available after 3pm, Need to leave early..."
            />
            <p className="text-xs text-gray-400 mt-1">{notes.length}/500 characters</p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 rounded-b-lg">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !status}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : 'Save Availability'}
          </button>
        </div>
      </div>
    </div>
  );
}
